import { Shield, Activity, AlertTriangle, CheckCircle, Clock, Users, Database, Zap, Server, Wifi, Lock, Globe } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useSEO } from '@/hooks/useSEO';
import { useAdmin } from '@/hooks/useAdmin';
import { ChessAppLayout } from '@/components/ChessAppLayout';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminLoginSessions } from '@/hooks/useAdminLoginSessions';
import AdminLoginLocationMap from '@/components/AdminLoginLocationMap';

interface SecurityMetrics {
  systemHealth: number;
  uptime: string;
  activeUsers: number;
  lastBackup: string;
  securityScore: number;
  threatLevel: 'low' | 'medium' | 'high';
  activeConnections: number;
  sslStatus: 'valid' | 'expiring' | 'invalid';
}

export default function AdminSecurityPage() {
  const [metrics, setMetrics] = useState<SecurityMetrics>({
    systemHealth: 98,
    uptime: '99.9%',
    activeUsers: 0,
    lastBackup: new Date().toISOString(),
    securityScore: 95,
    threatLevel: 'low',
    activeConnections: 0,
    sslStatus: 'valid'
  });
  const [isLoading, setIsLoading] = useState(true);

  useSEO({
    title: 'Security Dashboard - Admin Tools',
    description: 'Comprehensive security monitoring and system health dashboard for ChessHatch administrators.',
    canonical: '/admin/security',
    robots: 'noindex, nofollow'
  });

  const { isAdmin, loading } = useAdmin();
  const { sessions, loading: sessionsLoading, stats } = useAdminLoginSessions(30);

  useEffect(() => {
    if (isAdmin) {
      fetchSecurityMetrics();
    }
  }, [isAdmin]);

  const fetchSecurityMetrics = async () => {
    try {
      // Get active user count
      const { data: profiles, error } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact' });

      if (!error && profiles) {
        setMetrics(prev => ({
          ...prev,
          activeUsers: profiles.length || 0
        }));
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching security metrics:', error);
      setIsLoading(false);
    }
  };

  if (loading || isLoading) {
    return (
      <ChessAppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </ChessAppLayout>
    );
  }

  if (!isAdmin) {
    return (
      <ChessAppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <Shield className="h-16 w-16 text-destructive mx-auto" />
            <h1 className="text-3xl font-bold text-destructive">Access Denied</h1>
            <p className="text-muted-foreground max-w-md">
              You don't have permission to access the Security Dashboard.
            </p>
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  const getThreatColor = (level: string) => {
    switch (level) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid': return 'text-green-600';
      case 'expiring': return 'text-yellow-600';
      case 'invalid': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <ChessAppLayout>
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="p-3 rounded-2xl bg-primary/10">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <div className="text-left">
                <h1 className="text-3xl font-bold">Security Dashboard</h1>
                <p className="text-muted-foreground">System health and security monitoring</p>
              </div>
            </div>
          </div>

          {/* Security Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-bold text-green-700">{metrics.systemHealth}%</CardTitle>
                  <Activity className="h-5 w-5 text-green-600" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-green-600">System Health</p>
                <Progress value={metrics.systemHealth} className="mt-2" />
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-bold text-blue-700">{metrics.uptime}</CardTitle>
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-blue-600">Uptime (30 days)</p>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-bold text-purple-700">{metrics.activeUsers}</CardTitle>
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-purple-600">Registered Users</p>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-bold text-amber-700">{metrics.securityScore}</CardTitle>
                  <Lock className="h-5 w-5 text-amber-600" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-amber-600">Security Score</p>
                <Progress value={metrics.securityScore} className="mt-2" />
              </CardContent>
            </Card>
          </div>

          {/* Global Login Activity Map */}
          <Card className="col-span-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Global Login Activity
                  </CardTitle>
                  <CardDescription>Real-time map of all user login locations across the platform</CardDescription>
                </div>
                <div className="flex gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-bold text-lg">{stats.totalLogins}</div>
                    <div className="text-muted-foreground">Total Logins</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg">{stats.uniqueUsers}</div>
                    <div className="text-muted-foreground">Users</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg">{stats.uniqueCountries}</div>
                    <div className="text-muted-foreground">Countries</div>
                  </div>
                  {stats.suspiciousCount > 0 && (
                    <div className="text-center">
                      <div className="font-bold text-lg text-red-600">{stats.suspiciousCount}</div>
                      <div className="text-muted-foreground">Suspicious</div>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {sessionsLoading ? (
                <div className="w-full h-[500px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : sessions.length === 0 ? (
                <div className="w-full h-[500px] flex items-center justify-center bg-muted/20 rounded-lg">
                  <p className="text-muted-foreground">No login data available</p>
                </div>
              ) : (
                <>
                  <AdminLoginLocationMap sessions={sessions} />
                  <div className="mt-4 flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500 border-2 border-white shadow-lg"></div>
                      <span className="text-muted-foreground">Normal Login</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow-lg"></div>
                      <span className="text-muted-foreground">Suspicious Login</span>
                    </div>
                    <div className="text-muted-foreground ml-auto">
                      Showing logins from the last 30 days
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Detailed Security Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* System Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  System Status
                </CardTitle>
                <CardDescription>Real-time system health monitoring</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Database Connection
                  </span>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Healthy
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Wifi className="h-4 w-4" />
                    API Services
                  </span>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Online
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Performance
                  </span>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Optimal
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    SSL Certificate
                  </span>
                  <Badge variant="secondary" className={`${getStatusColor(metrics.sslStatus) === 'text-green-600' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {metrics.sslStatus === 'valid' ? 'Valid' : 'Expiring Soon'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Security Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Security Alerts
                </CardTitle>
                <CardDescription>Recent security events and notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span>Threat Level</span>
                  <Badge variant="secondary" className={`${getThreatColor(metrics.threatLevel) === 'text-green-600' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {metrics.threatLevel.toUpperCase()}
                  </Badge>
                </div>
                
                <div className="text-sm text-muted-foreground space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    No failed login attempts in 24h
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    All admin accounts secured with 2FA
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Database backups completed successfully
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    No suspicious IP activity detected
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Maintenance Checklist */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Maintenance Checklist
              </CardTitle>
              <CardDescription>Essential security maintenance tasks and their status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Core Security</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Software Updates</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Current</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">SSL Certificate</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Valid</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Firewall Status</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Active</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Malware Scan</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Clean</Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Monitoring</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Uptime Monitoring</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Active</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Security Logs</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Monitored</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Backup Verification</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Verified</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Performance Tests</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">Passing</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ChessAppLayout>
  );
}